import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

import "./ConfirmDialog.css";

/**
 * Common confirmation popup.
 *
 * Controlled component — the parent owns the `open` state and provides
 * `onConfirm` / `onCancel`. While `loading` is true the dialog stays open
 * with a spinner and every dismiss path (button, backdrop, Escape, close
 * icon) is disabled, so the user can't close it mid-action.
 *
 * <ConfirmDialog
 *   open={Boolean(target)}
 *   tone="danger"
 *   title="Delete document?"
 *   message={<>"{target?.name}" will be removed. This can't be undone.</>}
 *   confirmLabel="Delete"
 *   loading={deleting}
 *   onConfirm={runDelete}
 *   onCancel={() => setTarget(null)}
 * />
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  message = null,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger", // "danger" | "primary"
  loading = false,
  onConfirm,
  onCancel,
}) {
  const confirmBtnRef = useRef(null);

  // Keep the latest callbacks reachable from the key handler without
  // re-subscribing on every render.
  const onCancelRef = useRef(onCancel);
  const loadingRef = useRef(loading);
  onCancelRef.current = onCancel;
  loadingRef.current = loading;

  const requestCancel = () => {
    if (loading) return;
    onCancel?.();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loadingRef.current) {
        onCancelRef.current?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Lock background scroll while the dialog is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus to the confirm button.
    const focusTimer = window.setTimeout(() => {
      confirmBtnRef.current?.focus();
    }, 0);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="confirm-dialog-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          requestCancel();
        }
      }}
    >
      <div
        className={`confirm-dialog confirm-dialog--${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={
          message ? "confirm-dialog-message" : undefined
        }
      >
        <button
          type="button"
          className="confirm-dialog-close"
          onClick={requestCancel}
          disabled={loading}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <span className="confirm-dialog-icon" aria-hidden="true">
          <AlertTriangle size={22} />
        </span>

        <h2
          id="confirm-dialog-title"
          className="confirm-dialog-title"
        >
          {title}
        </h2>

        {message && (
          <div
            id="confirm-dialog-message"
            className="confirm-dialog-message"
          >
            {message}
          </div>
        )}

        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="confirm-dialog-btn confirm-dialog-btn--ghost"
            onClick={requestCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            ref={confirmBtnRef}
            className="confirm-dialog-btn confirm-dialog-btn--confirm"
            onClick={() => onConfirm?.()}
            disabled={loading}
          >
            {loading && (
              <span
                className="confirm-dialog-spinner"
                aria-hidden="true"
              />
            )}

            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
